import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    VStack,
    HStack,
    Button,
    Text,
    DataList,
    DataItem,
    Divider,
    Box,
    Icon,
    Textarea,
    prettyBytes,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useForm, useWatch } from 'react-hook-form';
import { AttachmentDto, UsageActionEnum } from '@mark43/cad-api';
import { useUpdateAttachment } from './hooks/use-update-attachment';
import { getFileName, getFileSize } from './helpers';
import { AttachmentDownloadInfo } from './types';
import { formatMonthDayYearHoursMinutes } from '../common/helpers/utils';
import { showDeleteAttachmentConfirmationModal } from './show-delete-attachment-confirmation-modal';
import { AttachmentDownloadButton } from './attachment-download-button';
import { useAttachmentUsageLog } from '../usage-logs/hooks/use-attachment-usage-log';

export interface AttachmentInfoSectionProps {
    selectedAttachment: AttachmentDto | undefined;
    entityId: number;
    entityType: 'CAD_AGENCY_EVENT' | 'BOLO';
    entityNumber?: string;
    onOpenEventPanel?: () => void;
    onOpenBoloPanel?: () => void;
    onDeleteAttachment: (
        attachmentId: number,
        attachmentType: AttachmentDto['attachmentType']
    ) => Promise<void>;
    isDeleting: boolean;
    hideDelete?: boolean;
    isEditable?: boolean;
}

interface AttachmentInfoFormValues {
    description: string;
}

const EMPTY_DESCRIPTION_HOVER_EFFECT = { bg: 'surface.secondary' };

export const AttachmentInfoSection = observer(function AttachmentInfoPanel({
    selectedAttachment,
    entityId,
    entityType,
    entityNumber,
    onOpenEventPanel,
    onOpenBoloPanel,
    onDeleteAttachment,
    isDeleting,
    hideDelete = false,
    isEditable = true,
}: AttachmentInfoSectionProps): React.ReactElement | null {
    const updateAttachmentMutation = useUpdateAttachment({ entityId, entityType });
    const { logAttachmentAction } = useAttachmentUsageLog({ entityId, entityType, entityNumber });

    const handleDownload = useCallback(
        function logDownload({ attachmentId, fileName }: AttachmentDownloadInfo) {
            logAttachmentAction(UsageActionEnum.DOWNLOADED_ATTACHMENT.name, attachmentId, fileName);
        },
        [logAttachmentAction]
    );

    const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
    const [editMode, setEditMode] = useState<'view' | 'edit'>('view');

    const { control, handleSubmit, reset, setValue } = useForm<AttachmentInfoFormValues>({
        defaultValues: {
            description: selectedAttachment?.description ?? '',
        },
    });

    const description = useWatch({
        control,
        name: 'description',
    });

    useEffect(
        function syncDescriptionWithAttachment() {
            reset({
                description: selectedAttachment?.description ?? '',
            });
            setEditMode('view');
        },
        [selectedAttachment?.attachmentId, selectedAttachment?.description, reset]
    );

    const onSubmit = useCallback(
        async function saveDescription(data: AttachmentInfoFormValues) {
            if (!selectedAttachment) return;

            try {
                await updateAttachmentMutation.mutateAsync({
                    attachmentId: selectedAttachment.attachmentId,
                    description: data.description,
                });
                setEditMode('view');
            } catch {
                setEditMode('view');
                reset({
                    description: selectedAttachment.description ?? '',
                });
            }
        },
        [selectedAttachment, updateAttachmentMutation, reset]
    );

    const handleDeleteClick = () => {
        if (!selectedAttachment) return;

        showDeleteAttachmentConfirmationModal({
            attachment: selectedAttachment,
            onConfirm: () => handleDeleteConfirm(),
            cancelButtonRef,
            isLoading: isDeleting,
        });
    };

    const handleDeleteConfirm = async () => {
        if (!selectedAttachment) return;

        try {
            await onDeleteAttachment(
                selectedAttachment.attachmentId,
                selectedAttachment.attachmentType
            );
        } catch {
            // Error is already handled by the mutation's onError
        }
    };

    if (!selectedAttachment) {
        return null;
    }

    const fileName = getFileName(selectedAttachment);
    const showNoDescriptionIcon = editMode === 'view' && !description;
    const showDisplayText = editMode === 'view' && description;
    const showTextArea = editMode === 'edit';

    const displayTextProps = isEditable
        ? {
              cursor: 'pointer',
              _hover: EMPTY_DESCRIPTION_HOVER_EFFECT,
          }
        : {};

    const enterEditMode = () => {
        isEditable && setEditMode('edit');
    };

    const fileSize = getFileSize(selectedAttachment);

    // fallback to `undefined` to show dash in `DataItem`
    const uploadDate = selectedAttachment.createdDateUtc
        ? formatMonthDayYearHoursMinutes(selectedAttachment.createdDateUtc)
        : undefined;
    return (
        <VStack
            spacing={0}
            flex={1}
            align="start"
            height="100%"
            position="relative"
            flexBasis="300px"
            flexGrow={0}
            minWidth={0}
            sx={{
                // overriding arc default styles that was applying unnecessary margin-left
                marginLeft: '0px !important',
            }}
        >
            <VStack spacing={2} flex={1} align="start" width="100%" overflow="auto" pb="80px">
                <Box pt={3} px={3} width="100%">
                    <Text fontSize="lg" fontWeight="bold">
                        Description
                    </Text>
                    {showNoDescriptionIcon && (
                        <Box
                            width="100%"
                            onClick={enterEditMode}
                            tabIndex={0}
                            aria-label="Add description"
                            {...displayTextProps}
                        >
                            <Icon size="sm" icon="Close" />
                        </Box>
                    )}
                    {showDisplayText && (
                        <Box
                            width="100%"
                            tabIndex={0}
                            aria-label="Description"
                            {...displayTextProps}
                        >
                            <Text onClick={enterEditMode}>{description}</Text>
                        </Box>
                    )}
                    {showTextArea && (
                        <Textarea
                            readOnly={updateAttachmentMutation.isLoading}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setValue('description', e.target.value)
                            }
                            onBlur={() => {
                                handleSubmit(onSubmit)();
                            }}
                            placeholder="Enter description..."
                            autoFocus
                            rows={4}
                            width="100%"
                            value={description}
                        />
                    )}
                </Box>
                <Divider />
                <Box
                    id="file-info-container"
                    pt={3}
                    px={3}
                    width="100%"
                    // overriding arc default styles that was applying unnecessary margin-top
                    sx={{ marginTop: '0px !important' }}
                >
                    <Text id="file-info-title" fontSize="lg" fontWeight="bold">
                        File Information
                    </Text>
                    <DataList>
                        <DataItem dataKey="Name">{fileName}</DataItem>
                        <DataItem dataKey="Uploaded Date">{uploadDate}</DataItem>
                        <DataItem dataKey="Uploaded By">{selectedAttachment.createdBy}</DataItem>
                        <DataItem dataKey="Size">{fileSize && prettyBytes(fileSize)}</DataItem>
                        {entityNumber && (
                            <DataItem
                                dataKey={entityType === 'BOLO' ? 'BOLO Number#' : 'Event Number#'}
                            >
                                {entityNumber}
                            </DataItem>
                        )}
                    </DataList>
                </Box>
                <Divider />

                <VStack
                    spacing={2}
                    width="100%"
                    p={3}
                    sx={{
                        // overriding arc default styles that was applying unnecessary margin-top
                        marginTop: '0px !important',
                    }}
                >
                    {entityType === 'CAD_AGENCY_EVENT' && onOpenEventPanel && (
                        <Button variant="outline" onClick={onOpenEventPanel} width="100%">
                            Open Event Panel
                        </Button>
                    )}
                    {entityType === 'BOLO' && onOpenBoloPanel && (
                        <Button variant="outline" onClick={onOpenBoloPanel} width="100%">
                            Open Bolo Panel
                        </Button>
                    )}
                </VStack>
            </VStack>
            <Divider />
            <HStack spacing={2} width="100%" p={4}>
                {!hideDelete && (
                    <Button
                        variant="ghost"
                        colorVariant="dangerous"
                        onClick={handleDeleteClick}
                        flex={1}
                        isDisabled={isDeleting}
                    >
                        Delete
                    </Button>
                )}
                <AttachmentDownloadButton
                    attachment={selectedAttachment}
                    onDownload={handleDownload}
                />
            </HStack>
        </VStack>
    );
});
